import React from 'react';
import { Phone, Clock } from 'lucide-react';
import { AgentCall } from '../types/agentCall';
import { formatDateTime, truncateText } from '../utils/textUtils';

interface AgentCallsListProps {
  calls: AgentCall[];
  title?: string;
  isLoading?: boolean;
  onRowClick?: (call: AgentCall) => void;
}

const getSourceLabel = (source: string | null): string => {
  if (!source) return 'Desconocido';

  const labels: Record<string, string> = {
    'elevenlabs_web': 'Web (ElevenLabs)',
    'elevenlabs_whatsapp': 'WhatsApp (ElevenLabs)',
    'elevenlabs_phone': 'Teléfono (ElevenLabs)'
  };

  return labels[source] || source;
};

const getChannelBadgeColor = (channel: string | null): string => {
  if (!channel) return 'bg-gray-100 text-gray-700';

  const colors: Record<string, string> = {
    'web': 'bg-blue-100 text-blue-700',
    'whatsapp': 'bg-green-100 text-green-700',
    'phone': 'bg-purple-100 text-purple-700'
  };

  return colors[channel.toLowerCase()] || 'bg-gray-100 text-gray-700';
};

const AgentCallsList: React.FC<AgentCallsListProps> = ({
  calls,
  title = 'Llamadas del asistente de Cero Ideas',
  isLoading = false,
  onRowClick
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          <p className="text-gray-600">Cargando llamadas del asistente...</p>
        </div>
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="text-center py-8">
          <Phone className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-gray-600">No hay llamadas registradas para este lead.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha/Hora
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Canal
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Origen
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duración
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Resumen
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {calls.map((call) => {
              const dateStr = call.started_at || call.created_at;
              const date = new Date(dateStr);
              const minutes = call.duration_seconds
                ? Math.round(call.duration_seconds / 60)
                : null;

              return (
                <tr
                  key={call.id}
                  onClick={() => onRowClick?.(call)}
                  className={onRowClick ? 'hover:bg-gray-50 cursor-pointer transition-colors' : ''}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatDateTime(date)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChannelBadgeColor(call.channel)}`}>
                      {call.channel || 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {getSourceLabel(call.source)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {minutes !== null ? (
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {minutes} min
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {call.summary ? truncateText(call.summary, 100) : (
                      <span className="text-gray-400 italic">Sin resumen</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgentCallsList;
